import { createMentionSmartLink } from 'common/Prose/utils/smartLinks/utils'
import { Typeahead } from 'components/Typeahead/Typeahead'
import { getHistoryInstance } from 'hooks/useHistory'
import { Plugin, PluginKey, Transaction } from 'prosemirror-state'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import queryClient from 'queryClient'
import { createRoot, Root } from 'react-dom/client'
import { QueryClientProvider } from 'react-query'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'
import store from 'redux/store'
import { ArticleFlat } from 'types'
import Styles from './MentionPrompt.module.scss'

export const TRIGGER_CHAR = '@'
const TRIGGER_REGEX = new RegExp(`(\\(| )${TRIGGER_CHAR}`)
const DECORATION_KEY = 'mentionTypeahead'
export const MENTION_PROMPT_PLUGIN_KEY = new PluginKey('mentionPrompt')

let currentQuery = ''

const isTrigger = (tr: Transaction): boolean => {
  if (tr.getMeta('source') === 'remote') {
    return false
  }
  if (tr.steps.length === 1) {
    const stepJSON = tr.steps[0].toJSON()
    if (stepJSON.stepType === 'replace' && stepJSON.slice?.content[0].text === TRIGGER_CHAR) {
      return true
    }
  }

  return false
}

export interface MentionPromptPluginConfig {
  mentioningArticle?: ArticleFlat
  allowCreateNewFile?: boolean
  suggestRecentlyReadArticle?: boolean
  dropdownWidth?: string
  overrideMentionPromptClassname?: string
}

const MentionPromptPlugin = (pluginConfig: MentionPromptPluginConfig) => {
  return new Plugin({
    key: MENTION_PROMPT_PLUGIN_KEY,

    props: {
      handleDOMEvents: {
        keydown: (view, event) => {
          const state = MENTION_PROMPT_PLUGIN_KEY.getState(view.state)

          if (state && state.active && (event as KeyboardEvent).key === 'Enter') {
            return true
          }

          if (state && state.active && (event as KeyboardEvent).key === 'Escape') {
            return true
          }

          return false
        },
      },
      editable(state) {
        return !MENTION_PROMPT_PLUGIN_KEY.getState(state).active
      },
      decorations(editorState) {
        return MENTION_PROMPT_PLUGIN_KEY.getState(editorState).decorations
      },
    },

    state: {
      init(config) {
        const widgetContainer = document.createElement('div')
        widgetContainer.className = Styles.container
        return {
          active: false,
          decorations: DecorationSet.create(config.doc, []),
          widgetContainer,
          root: createRoot(widgetContainer),
        }
      },

      apply(tr, state, _oldEditorState, newEditorState) {
        if (
          state.active &&
          state.decorations.find(undefined, undefined, ({ key }: { key: string }) => key === DECORATION_KEY).length ===
            0
        ) {
          currentQuery = ''
          return {
            ...state,
            active: false,
          }
        }

        const meta = tr.getMeta(MENTION_PROMPT_PLUGIN_KEY)
        if (meta && meta.active === false) {
          currentQuery = ''
          const decorationsToRemove = state.decorations.find()
          state.root.render(<></>)
          return {
            ...state,
            active: false,
            decorations: state.decorations.remove(decorationsToRemove),
          }
        }

        if (
          isTrigger(tr) &&
          newEditorState.selection.$from.node().type.contentMatch.matchType(newEditorState.schema.nodes.smartLink)
        ) {
          currentQuery = meta?.cachedAttributes?.title || ''

          const triggerPos = tr.selection.$from.pos - 1
          const $triggerPos = tr.doc.resolve(triggerPos)
          // NOTE: only trigger mention prompt when we are at the first
          // position of a node or the trigger character (@) is preceeded by a
          // whitespace
          if (
            $triggerPos.start() === $triggerPos.pos ||
            tr.doc.textBetween(triggerPos - 1, triggerPos + 1).match(TRIGGER_REGEX)
          ) {
            return {
              ...state,
              active: true,
              decorations: state.decorations.add(tr.doc, [
                Decoration.widget(triggerPos, renderWidget(state.root, state.widgetContainer, pluginConfig), {
                  key: DECORATION_KEY,
                }),
              ]),
              editingNode: meta?.editingNode,
            }
          }
        }
        if (tr.getMeta('source') === 'remote') {
          return {
            ...state,
            decorations: state.decorations.map(tr.mapping, tr.doc),
          }
        }
        return { ...state }
      },
    },

    // When the user writes TRIGGER_CHAR, we want to transform that
    // TRIGGER_CHAR into the typeahead input, and not leave any hanging
    // TRIGGER_CHAR for the current editor and other collaborators.
    appendTransaction(transactions, _, newState) {
      const pluginState = MENTION_PROMPT_PLUGIN_KEY.getState(newState)
      const tr = transactions[0]

      if (!isTrigger(tr) || !pluginState.active) {
        return
      }

      const parent = newState.selection.$from.parent

      // If the parent is a text block (for example the initial paragraph) and
      // is empty, insert a space character. Text blocks cannot be totally
      // empty.
      if (parent.isTextblock && parent.textContent === TRIGGER_CHAR) {
        return newState.tr.replaceWith(tr.selection.from - TRIGGER_CHAR.length, tr.selection.to, [
          newState.schema.text(' '),
        ])
      } else {
        return newState.tr.delete(tr.selection.from - TRIGGER_CHAR.length, tr.selection.to)
      }
    },
  })
}

export interface SelectionAttributes {
  last_edited_at?: string | null | number
  last_edited_by?: string | null
  type: 'Article' | 'User' | 'Handbook' | 'Folder'
  url: string
  title: string
  key?: string
  userId?: string
  header_emoji?: string
}

const renderWidget =
  (root: Root, container: HTMLElement, config: MentionPromptPluginConfig) =>
  (view: EditorView, getPos: () => number) => {
    const {
      mentioningArticle,
      allowCreateNewFile,
      suggestRecentlyReadArticle,
      dropdownWidth,
      overrideMentionPromptClassname,
    } = config

    const insertMention = (attributes: SelectionAttributes) => {
      const node = createMentionSmartLink(view, attributes)
      const { tr } = view.state
      if (view.state.selection.$from.parent.textContent === ' ') {
        tr.delete(view.state.selection.$from.pos, view.state.selection.$to.pos + 1)
      }
      tr.insert(getPos(), node)
      tr.setMeta(MENTION_PROMPT_PLUGIN_KEY, { active: false })
      view.dispatch(tr)
      view.focus()
    }

    const insertTriggerChar = () => {
      const tr = view.state.tr.insert(getPos(), [view.state.schema.text(TRIGGER_CHAR)])
      tr.setMeta(MENTION_PROMPT_PLUGIN_KEY, { active: false })
      view.dispatch(tr)
      view.focus()
    }

    const onCancel = () => {
      const pluginState = MENTION_PROMPT_PLUGIN_KEY.getState(view.state)
      if (pluginState.editingNode) {
        insertMention(pluginState.editingNode.attrs)
      } else {
        insertTriggerChar()
      }
    }

    root.render(
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <Router history={getHistoryInstance()}>
            <Typeahead
              initialQuery={currentQuery}
              onChange={(query) => (currentQuery = query)}
              onCancel={onCancel}
              onSelect={insertMention}
              mentioningArticle={mentioningArticle}
              suggestRecentlyReadArticle={suggestRecentlyReadArticle}
              allowCreateNewFile={allowCreateNewFile}
              dropdownWidth={dropdownWidth}
              overrideMentionPromptClassname={overrideMentionPromptClassname}
            />
          </Router>
        </QueryClientProvider>
      </Provider>,
    )

    return container
  }

export default MentionPromptPlugin
